import { ReactNode } from 'react';
import { Cell, Header } from '@tanstack/react-table';

type Renderable<TProps> = ReactNode | ((props: TProps) => ReactNode);

/**
 * Alternative to flexRender from react-table
 *
 * That uses JSX to render the first argument, so treats it as a component.
 * If the function is declared inline, this makes a new component each time (so loses state).
 *
 * This calls the first argument as a function.
 * Downside is that it can't use hooks directly (but can in a component it uses),
 * upside is that it allows the component being called to track state.
 *
 * @param render Either a node that can be rendered directly, or a function returning a node
 * @param props Props to be passed to the `render` if it is a function
 * @returns a react node
 */
export function flexRender<TProps extends object>(render: Renderable<TProps>, props: TProps) {
  return !render ? null : typeof render === 'function' ? render(props) : render;
}

export function renderCellContents<TData, TValue>(cell: Cell<TData, TValue>) {
  return flexRender(cell.column.columnDef.cell, cell.getContext());
}

export function renderHeaderContents<TData>(header: Header<TData, unknown>) {
  return flexRender(header.column.columnDef.header, header.getContext());
}

export function renderFooterContents<TData>(header: Header<TData, unknown>) {
  return flexRender(header.column.columnDef.footer, header.getContext());
}
